import React from 'react';
import { Agent, User, Message } from '../types';
import AgentIcon from './AgentIcon';
import ProductSearchlightModeToggle, {
  type ProductSearchlightMode,
} from './products/ProductSearchlightModeToggle';

import './TopNavbar.css';

interface TopNavbarProps {
  agent: Agent | null;
  user: User;
  onBackToAgents: () => void;
  isAgentTyping?: boolean;
  messages?: Message[];
  a2aRouting?: {
    originalAgent: string;
    currentAgent: string;
    previousAgent?: string;
    reason?: string;
  } | null;
  onClearChat?: () => void;
  onCopyConversation?: () => void;
  onToggleSidebar?: () => void;
  isSidebarOpen?: boolean;
  isDirectAgentAccess?: boolean; // When user accessed agent directly via agent card
  productSearchlightMode?: ProductSearchlightMode;
  onProductSearchlightModeChange?: (mode: ProductSearchlightMode) => void;
  showProductSearchlightModeToggle?: boolean;
}

const TopNavbar = React.memo<TopNavbarProps>(({
  agent,
  user,
  onBackToAgents,
  isAgentTyping = false,
  messages = [],
  a2aRouting = null,
  onClearChat,
  onCopyConversation,
  onToggleSidebar,
  isSidebarOpen = false,
  isDirectAgentAccess = false,
  productSearchlightMode = 'chat',
  onProductSearchlightModeChange,
  showProductSearchlightModeToggle = false,
}) => {
  if (!agent) {
    return (
      <div className="bg-transparent px-3 sm:px-4 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {onToggleSidebar && (
              <button
                onClick={onToggleSidebar}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-teal-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                title={isSidebarOpen ? "Close menu" : "Open menu"}
              >
                {isSidebarOpen ? '✕' : '☰'}
              </button>
            )}
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Scout
            </h1>
          </div>
        </div>
      </div>
    );
  }

  // Determine what name to display
  // - Direct agent access (via agent card): show that specific agent's name
  // - Normal Scout chat: ALWAYS show "Scout Central" regardless of which agent Scout routes to
  //   (A2A routing is transparent to the user in concierge mode)
  let displayName = 'Scout Central';
  let showAgentBadge = false;
  
  if (isDirectAgentAccess && agent && !agent.hidden) {
    // Direct access to a specific agent via agent card - show their name prominently
    displayName = agent.name;
    showAgentBadge = true;
  }
  // In normal Scout mode, displayName stays as "Scout Central" even if Scout routes to specialists

  return (
    <div className="bg-transparent px-3 sm:px-4 py-3 sm:py-4">
      <div className="flex items-center justify-between min-w-0">
        <div className="flex items-center min-w-0">
          {onToggleSidebar && (
            <button
              onClick={onToggleSidebar}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-teal-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0"
              title={isSidebarOpen ? "Close menu" : "Open menu"}
            >
              {isSidebarOpen ? '✕' : '☰'}
            </button>
          )}
          
          <button
            onClick={onBackToAgents}
            className="flex items-center gap-1 sm:gap-2 text-gray-600 dark:text-gray-400 hover:text-teal-500 transition-colors min-w-0 flex-shrink-0 ml-2"
          >
            <span>←</span>
            <span className="text-sm hidden sm:inline">Back</span>
          </button>
        </div>
        
        {/* Center: Agent name/identity */}
        <div className="flex items-center gap-2 flex-shrink min-w-0">
          {displayName === 'Scout Central' ? (
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white truncate">
              Scout
            </h1>
          ) : (
            <>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white truncate">
                {displayName}
              </h1>
              {showProductSearchlightModeToggle && onProductSearchlightModeChange && (
                <ProductSearchlightModeToggle
                  mode={productSearchlightMode}
                  onChange={onProductSearchlightModeChange}
                />
              )}
            </>
          )}
        </div>
        
        {/* Right side: placeholder for future actions */}
        <div className="w-16 flex-shrink-0" />
      </div>
    </div>
  );
});

export default TopNavbar;
